import { environment } from '@environments/environment';
import { Image, MAX_PIXEL_DENSITY, Size } from '@shared/components/image/models';
import { getGravity } from '@shared/components/image/utils/get-gravity';
import { getSizes } from '@shared/components/image/utils/get-sizes';
import { LogsService } from '@shared/services/logs';

const CDN_URL: string | undefined = environment.CDN?.url;
const KEEPEEK_INSTANCES = environment.CDN.keepeekInstances;

/**
 * Rewrites a Keepeek media URL into its CDN equivalent, appending the transformation segment
 * (format, crop, device pixel ratio, size) when an image size is requested. SVG files are served
 * untransformed, and an Instagram media is proxied by the CDN under its own path.
 *
 * @param media - An `Image`, a plain URL string, or a nullish value.
 * @param imageSize - Resize dimensions. Omitted, the returned URL carries no transformation.
 * @param devicePixelRatio - Screen pixel ratio, added to the transformation when above 1 and
 * capped at `MAX_PIXEL_DENSITY`.
 *
 * @returns The CDN URL; the input URL untouched when the media comes from neither Keepeek nor
 * Instagram; an empty string when the media or the CDN configuration is unusable.
 *
 * The alias comes from the instance the media is hosted on, never from the environment: a page
 * served by the test site can carry an image hosted on the production Keepeek instance.
 */
export const getCDNLink = (
  media: Image | string | null | undefined,
  imageSize?: Size,
  devicePixelRatio = 1
): string => {
  if (!media || !CDN_URL) {
    if (!CDN_URL) {
      LogsService.error('getCDNLink', 'CDN environment variable (url) is not configured.');
    }
    return '';
  }

  const link = typeof media === 'string' ? media : media.link;

  if (!link) {
    return '';
  }

  const keepeek = KEEPEEK_INSTANCES.find(({ url }) => link.startsWith(url));

  if (!keepeek && !link.startsWith('insta')) {
    return link;
  }

  const src = keepeek ? link.replace(keepeek.url, keepeek.alias) : link;

  if (!imageSize) {
    return `${CDN_URL}${src.startsWith('/') ? '' : '/'}${src}`;
  }

  const params: string[] = ['format=auto', 'fit=cover'];

  const isSvg = link?.toLowerCase().endsWith('.svg');
  const density = Math.min(devicePixelRatio, MAX_PIXEL_DENSITY);
  if (density !== 1 && !isSvg) {
    params.push(`dpr=${density}`);
  }

  if (typeof media !== 'string' && media.gravity && !isSvg) {
    const gravityValue = getGravity(media.gravity);
    if (gravityValue) params.push(gravityValue);
  }

  if (!isSvg) {
    const sizeValue = getSizes(imageSize);
    if (sizeValue) params.push(sizeValue);
  }

  const transformSegment = `/cdn-cgi/image/${params.join(',')}`;

  return `${CDN_URL}${transformSegment}${src.startsWith('/') ? '' : '/'}${src}`;
};
